import { useState, useEffect, useMemo } from 'react';
import { db } from '../../../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';

/**
 * Hook to fetch and manage OE adjustments from Hugo_OEAdjustments.
 * Subscribes to active, non-expired adjustments for the given organization.
 * Merges adjustment deltas with base OE scores to produce enrichedScores.
 *
 * @param {string} organizationId - The organization to fetch adjustments for
 * @param {object|null} baseScores - Base OE scores from useOEScores (latestScores)
 *   Expected shape: { overall, reliability, efficiency, productivity, safety }
 * @returns {{ adjustments, enrichedScores, loading, error }}
 */
export function useOEAdjustments(organizationId, baseScores = null) {
  const [rawAdjustments, setRawAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setRawAdjustments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Query active adjustments for this organization, ordered by expiration
    const q = query(
      collection(db, 'Hugo_OEAdjustments'),
      where('organizationId', '==', organizationId),
      where('status', '==', 'active'),
      orderBy('expiresAt', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const adjustmentData = [];
        querySnapshot.forEach((doc) => {
          adjustmentData.push({ id: doc.id, ...doc.data() });
        });
        setRawAdjustments(adjustmentData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching OE adjustments:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [organizationId]);

  // Filter out expired adjustments client-side
  const adjustments = useMemo(() => {
    const now = Timestamp.now();
    return rawAdjustments.filter((adj) => {
      if (!adj.expiresAt) return true; // No expiry means always active
      // Compare Firestore timestamps
      const expiresAt = adj.expiresAt?.toMillis
        ? adj.expiresAt.toMillis()
        : new Date(adj.expiresAt).getTime();
      return expiresAt > now.toMillis();
    });
  }, [rawAdjustments]);

  // Merge base scores with adjustment deltas to produce enriched scores
  const enrichedScores = useMemo(() => {
    if (!baseScores) return null;

    const QUADRANTS = ['reliability', 'efficiency', 'productivity', 'safety'];

    // Start with a copy of base scores
    const enriched = { ...baseScores };

    // Sum all REPS deltas from active adjustments
    const totalDeltas = { reliability: 0, efficiency: 0, productivity: 0, safety: 0 };

    for (const adj of adjustments) {
      const deltas = adj.repsDeltas || adj.deltas || {};
      for (const quad of QUADRANTS) {
        if (typeof deltas[quad] === 'number') {
          totalDeltas[quad] += deltas[quad];
        }
      }
    }

    // Apply deltas to each quadrant score
    let overallSum = 0;
    let quadCount = 0;

    for (const quad of QUADRANTS) {
      const baseVal = typeof enriched[quad] === 'number' ? enriched[quad] : 0;
      // Clamp adjusted score between 0 and 10 (or whatever the scale max is)
      enriched[quad] = Math.max(0, Math.min(10, baseVal + totalDeltas[quad]));
      overallSum += enriched[quad];
      quadCount++;
    }

    // Recalculate overall as average of adjusted quadrant scores
    if (quadCount > 0) {
      enriched.overall = parseFloat((overallSum / quadCount).toFixed(2));
    }

    // Include metadata about adjustments applied
    enriched._adjustmentsApplied = adjustments.length;
    enriched._totalDeltas = totalDeltas;

    return enriched;
  }, [baseScores, adjustments]);

  return { adjustments, enrichedScores, loading, error };
}

export default useOEAdjustments;
